/**
 * V5.3: 공휴일 캘린더 뷰
 *
 * 기능:
 * - 월별 캘린더 형태로 공휴일 표시
 * - 월 네비게이션 (이전/다음/오늘)
 * - 공휴일 클릭 시 상세/수정
 * - 유형별 색상 구분
 *
 * V5.3 변경:
 * - Tailwind 클래스 제거, CSS 모듈로 전환
 * - 로딩 상태 UI 개선
 */

import React, { useState, useMemo } from 'react';
import {
  ChevronLeft,
  ChevronRight,
  Lock,
} from 'lucide-react';
import {
  Holiday,
  HolidayType,
  HOLIDAY_TYPE_LABELS,
  DAY_OF_WEEK_LABELS,
} from './types';
import styles from './holidaysStyles.module.css';

interface HolidayCalendarViewProps {
  holidays: Holiday[];
  selectedYear: number;
  isLoading: boolean;
  onHolidayClick?: (holiday: Holiday) => void; // V60: optional for read-only
}

export function HolidayCalendarView({
  holidays,
  selectedYear,
  isLoading,
  onHolidayClick,
}: HolidayCalendarViewProps) {
  // 현재 표시 중인 월 (0-11)
  const [currentMonth, setCurrentMonth] = useState(() => {
    const today = new Date();
    return today.getFullYear() === selectedYear ? today.getMonth() : 0;
  });

  // 연도 변경 시 월 리셋
  React.useEffect(() => {
    const today = new Date();
    setCurrentMonth(today.getFullYear() === selectedYear ? today.getMonth() : 0);
  }, [selectedYear]);

  // 월별 공휴일 맵 생성
  const holidaysByDate = useMemo(() => {
    const map = new Map<string, Holiday[]>();
    holidays.forEach((holiday) => {
      const existing = map.get(holiday.date) || [];
      map.set(holiday.date, [...existing, holiday]);
    });
    return map;
  }, [holidays]);

  // 해당 월의 일수와 시작 요일 계산
  const { daysInMonth, startDayOfWeek, prevMonthDays, nextMonthDays } = useMemo(() => {
    const firstDay = new Date(selectedYear, currentMonth, 1);
    const lastDay = new Date(selectedYear, currentMonth + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startDayOfWeek = firstDay.getDay();

    // 이전 달 날짜들 (시작 요일 채우기)
    const prevMonthLastDay = new Date(selectedYear, currentMonth, 0).getDate();
    const prevMonthDays: number[] = [];
    for (let i = startDayOfWeek - 1; i >= 0; i--) {
      prevMonthDays.push(prevMonthLastDay - i);
    }

    // 다음 달 날짜들 (마지막 행 채우기)
    const totalCells = Math.ceil((startDayOfWeek + daysInMonth) / 7) * 7;
    const nextMonthDays: number[] = [];
    for (let i = 1; i <= totalCells - startDayOfWeek - daysInMonth; i++) {
      nextMonthDays.push(i);
    }

    return { daysInMonth, startDayOfWeek, prevMonthDays, nextMonthDays };
  }, [selectedYear, currentMonth]);

  // 월 네비게이션
  const goToPrevMonth = () => {
    if (currentMonth === 0) return; // 선택한 연도 범위 내에서만
    setCurrentMonth((prev) => prev - 1);
  };

  const goToNextMonth = () => {
    if (currentMonth === 11) return;
    setCurrentMonth((prev) => prev + 1);
  };

  const goToToday = () => {
    const today = new Date();
    if (today.getFullYear() === selectedYear) {
      setCurrentMonth(today.getMonth());
    }
  };

  // 유형별 공휴일 아이템 클래스
  const getHolidayItemClass = (type: HolidayType): string => {
    switch (type) {
      case 'national':
        return styles.calendarHolidayItemNational;
      case 'company':
        return styles.calendarHolidayItemCompany;
      case 'temporary':
        return styles.calendarHolidayItemTemporary;
      default:
        return styles.calendarHolidayItemTemporary;
    }
  };

  // 날짜 포맷
  const formatDateKey = (year: number, month: number, day: number): string => {
    const m = String(month + 1).padStart(2, '0');
    const d = String(day).padStart(2, '0');
    return `${year}-${m}-${d}`;
  };

  // 오늘 날짜
  const today = new Date();
  const isToday = (day: number): boolean =>
    today.getFullYear() === selectedYear &&
    today.getMonth() === currentMonth &&
    today.getDate() === day;

  // 요일 헤더 클래스
  const getWeekDayClass = (idx: number): string => {
    if (idx === 0) return styles.calendarWeekDaySunday;
    if (idx === 6) return styles.calendarWeekDaySaturday;
    return styles.calendarWeekDay;
  };

  // 로딩 상태
  if (isLoading) {
    return (
      <div className={styles.loadingState}>
        <div className={styles.loadingStateContent}>
          <div className={styles.loadingSpinner} />
          <span className={styles.loadingText}>캘린더를 불러오는 중...</span>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.calendarContainer}>
      {/* 헤더: 월 네비게이션 */}
      <div className={styles.calendarHeader}>
        <div className={styles.calendarNav}>
          <button
            onClick={goToPrevMonth}
            disabled={currentMonth === 0}
            className={styles.calendarNavButton}
          >
            <ChevronLeft />
          </button>
          <h3 className={styles.calendarTitle}>
            {selectedYear}년 {currentMonth + 1}월
          </h3>
          <button
            onClick={goToNextMonth}
            disabled={currentMonth === 11}
            className={styles.calendarNavButton}
          >
            <ChevronRight />
          </button>
        </div>

        <button
          onClick={goToToday}
          className={styles.calendarTodayButton}
        >
          오늘
        </button>
      </div>

      {/* 요일 헤더 */}
      <div className={styles.calendarWeekHeader}>
        {DAY_OF_WEEK_LABELS.map((day, idx) => (
          <div key={day} className={getWeekDayClass(idx)}>
            {day}
          </div>
        ))}
      </div>

      {/* 캘린더 그리드 */}
      <div className={styles.calendarGrid}>
        {/* 이전 달 날짜들 */}
        {prevMonthDays.map((day) => (
          <div key={`prev-${day}`} className={styles.calendarCellOther}>
            <span className={styles.calendarCellDate}>{day}</span>
          </div>
        ))}

        {/* 현재 달 날짜들 */}
        {Array.from({ length: daysInMonth }, (_, i) => i + 1).map((day) => {
          const dateKey = formatDateKey(selectedYear, currentMonth, day);
          const dayHolidays = holidaysByDate.get(dateKey) || [];
          const dayOfWeek = new Date(selectedYear, currentMonth, day).getDay();
          const isSunday = dayOfWeek === 0;
          const isSaturday = dayOfWeek === 6;
          const hasNationalHoliday = dayHolidays.some((h) => h.type === 'national');
          const isTodayCell = isToday(day);

          // 날짜 숫자 클래스 결정
          let dateClass = styles.calendarCellDate;
          if (isTodayCell) {
            dateClass = styles.calendarCellDateToday;
          } else if (hasNationalHoliday || isSunday) {
            dateClass = `${styles.calendarCellDate} ${styles.calendarCellDateHoliday}`;
          } else if (isSaturday) {
            dateClass = `${styles.calendarCellDate} ${styles.calendarCellDateSaturday}`;
          }

          return (
            <div
              key={day}
              className={`${styles.calendarCell} ${isTodayCell ? styles.calendarCellToday : ''}`}
            >
              {/* 날짜 */}
              <div>
                <span className={dateClass}>{day}</span>
              </div>

              {/* 공휴일 목록 */}
              <div className={styles.calendarHolidayList}>
                {dayHolidays.slice(0, 3).map((holiday) => (
                  <button
                    key={holiday.id}
                    onClick={() => onHolidayClick?.(holiday)}
                    className={getHolidayItemClass(holiday.type)}
                    title={`${holiday.name} (${HOLIDAY_TYPE_LABELS[holiday.type]})`}
                  >
                    {holiday.type === 'national' && (
                      <Lock className={styles.calendarHolidayItemLock} />
                    )}
                    <span className={styles.calendarHolidayItemName}>{holiday.name}</span>
                  </button>
                ))}
                {dayHolidays.length > 3 && (
                  <div className={styles.calendarHolidayMore}>
                    +{dayHolidays.length - 3}개 더
                  </div>
                )}
              </div>
            </div>
          );
        })}

        {/* 다음 달 날짜들 */}
        {nextMonthDays.map((day) => (
          <div key={`next-${day}`} className={styles.calendarCellOther}>
            <span className={styles.calendarCellDate}>{day}</span>
          </div>
        ))}
      </div>

      {/* 범례 */}
      <div className={styles.calendarLegend}>
        <span className={styles.calendarLegendLabel}>범례:</span>
        <div className={styles.calendarLegendItem}>
          <span className={styles.calendarLegendDotRed}></span>
          <span className={styles.calendarLegendText}>법정공휴일</span>
        </div>
        <div className={styles.calendarLegendItem}>
          <span className={styles.calendarLegendDotBlue}></span>
          <span className={styles.calendarLegendText}>회사행사</span>
        </div>
        <div className={styles.calendarLegendItem}>
          <span className={styles.calendarLegendDotGray}></span>
          <span className={styles.calendarLegendText}>임시휴일</span>
        </div>
        <div className={styles.calendarLegendLock}>
          <Lock />
          <span className={styles.calendarLegendText}>보호됨 (이름만 수정 가능)</span>
        </div>
      </div>
    </div>
  );
}
